import { type FormEvent, type JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Button, Input, Spinner } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useProviders } from '@/hooks/useAdminProviders';
import {
  type Rate,
  type RateMessageType,
} from '@/hooks/useAdminRates';

// Per ADR-008 each rate carries BOTH the carrier (provider_id) and the
// price (buy_cost + sell_price). Shape-key fields (prefix, message_type,
// provider_id, effective_from) are locked on edit because the unique
// index keys on (card, prefix, message_type, provider, effective_from);
// changing one of those would always upsert into a different row.
const MESSAGE_TYPES: ReadonlyArray<RateMessageType> = ['marketing', 'transactional', '2fa'];

export interface RateFormSubmitInput {
  name?: string;
  prefix: string;
  message_type?: RateMessageType | '';
  /** Undefined on reseller cards — derived from the wholesale card. */
  provider_id?: number;
  /** Undefined on reseller cards — taken from the wholesale card. */
  buy_cost?: number;
  sell_price: number;
  priority: number;
  effective_from?: string;
  effective_to?: string;
  is_active: boolean;
  notes?: string;
}

interface RateFormModalProps {
  editing: Rate | null;
  onClose: () => void;
  onSubmit: (input: RateFormSubmitInput) => Promise<void>;
  submitting: boolean;
  /**
   * True on a reseller-owned card: the carrier and the buy cost are
   * derived server-side from the wholesale card the platform assigned,
   * so neither is asked for here. Resellers author only the price they
   * charge their own customers.
   */
  derivedCarrier: boolean;
}

export function RateFormModal({
  editing,
  onClose,
  onSubmit,
  submitting,
  derivedCarrier,
}: RateFormModalProps): JSX.Element {
  const { t } = useTranslation('default');
  const isEdit = editing !== null;
  // Platform-only endpoint — a reseller gets 403, so don't even ask.
  const providers = useProviders({ enabled: !derivedCarrier });

  const [name, setName] = useState(editing?.name ?? '');
  const [prefix, setPrefix] = useState(editing?.prefix ?? '');
  const [messageType, setMessageType] = useState<RateMessageType | ''>(
    editing?.message_type ?? '',
  );
  const [providerId, setProviderId] = useState(editing?.provider_id ?? 0);
  const [buyCost, setBuyCost] = useState(editing?.buy_cost.toString() ?? '');
  const [sellPrice, setSellPrice] = useState(editing?.sell_price.toString() ?? '');
  const [priority, setPriority] = useState((editing?.priority ?? 100).toString());
  const [effectiveFrom, setEffectiveFrom] = useState(
    editing ? toDateInputValue(editing.effective_from) : '',
  );
  const [effectiveTo, setEffectiveTo] = useState(
    editing?.effective_to ? toDateInputValue(editing.effective_to) : '',
  );
  const [isActive, setIsActive] = useState(editing?.is_active ?? true);
  const [notes, setNotes] = useState(editing?.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  const buyNum = parseFloat(buyCost);
  const sellNum = parseFloat(sellPrice);
  const margin = Number.isFinite(buyNum) && Number.isFinite(sellNum) ? sellNum - buyNum : 0;
  const marginPct = Number.isFinite(buyNum) && buyNum > 0 ? (margin / buyNum) * 100 : 0;
  const sellBelowBuy = Number.isFinite(buyNum) && Number.isFinite(sellNum) && sellNum < buyNum;

  const selectedProvider = providers.data?.items.find((p) => p.id === providerId);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    const prefixClean = prefix.trim();
    if (prefixClean === '') {
      setError(t('ar_err_prefix_required'));
      return;
    }
    if (prefixClean !== '*' && !/^\d{1,18}$/.test(prefixClean)) {
      setError(t('ar_err_prefix'));
      return;
    }
    if (!derivedCarrier && providerId === 0) {
      setError(t('ar_err_carrier'));
      return;
    }
    if (!derivedCarrier && (!Number.isFinite(buyNum) || buyNum < 0)) {
      setError(t('ar_err_cost'));
      return;
    }
    if (!Number.isFinite(sellNum) || sellNum < 0) {
      setError(t('ar_err_price'));
      return;
    }
    // Margin is only checkable client-side when the caller states the
    // cost. On a reseller card the wholesale cost lives server-side, so
    // the floor check happens there and comes back as a 422.
    if (!derivedCarrier && sellNum < buyNum) {
      setError(t('ar_err_negative_margin'));
      return;
    }
    // effective_from preservation: when editing, the datetime-local
    // input drops seconds + sub-seconds + interprets in local TZ. If
    // the user didn't touch the field, re-serializing the input value
    // produces a DIFFERENT ISO than the original — which breaks the
    // backend's upsert conflict key (rate_card_id, prefix,
    // message_type, provider_id, effective_from) and inserts a
    // duplicate row instead of updating. Detect "field unchanged" and
    // send the original ISO verbatim.
    let effectiveFromIso: string | undefined;
    if (effectiveFrom) {
      const original = editing?.effective_from ?? '';
      const originalAsInput = original ? toDateInputValue(original) : '';
      // If the form value matches what we rendered from the original,
      // the user didn't touch it — send the original ISO unchanged.
      if (editing && effectiveFrom === originalAsInput) {
        effectiveFromIso = original;
      } else {
        effectiveFromIso = new Date(effectiveFrom).toISOString();
      }
    }

    try {
      await onSubmit({
        name: name.trim() || undefined,
        prefix: prefixClean,
        message_type: messageType || undefined,
        provider_id: derivedCarrier ? undefined : providerId,
        buy_cost: derivedCarrier ? undefined : buyNum,
        sell_price: sellNum,
        priority: parseInt(priority, 10) || 100,
        effective_from: effectiveFromIso,
        effective_to: effectiveTo ? new Date(effectiveTo).toISOString() : undefined,
        is_active: isActive,
        notes: notes.trim() || undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('ar_err_generic'));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ink/50 px-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-line rounded-lg shadow-lg w-full max-w-[640px] max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={(e) => void submit(e)}>
          <div className="px-5 py-4 border-b border-line">
            <h2 className="display text-[16px] font-semibold text-text">
              {isEdit ? t('ar_form_edit_title') : t('ar_form_new_title')}
            </h2>
            <p className="text-[12px] text-text-3 mt-0.5">{t('ar_form_sub')}</p>
          </div>
          <div className="px-5 py-4 space-y-4">
            {error && (
              <div className="px-3 py-2 bg-danger-bg border border-danger-bd rounded-md text-[13px] text-danger">
                {error}
              </div>
            )}

            <Field label={t('ar_field_name')} hint={t('ar_field_name_hint')}>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="US — Bandwidth Florida"
                maxLength={120}
              />
            </Field>

            <div className="grid grid-cols-3 gap-3">
              <Field label={t('ar_field_prefix')} hint={t('ar_field_prefix_hint')}>
                <Input
                  value={prefix}
                  onChange={(e) => setPrefix(e.target.value)}
                  placeholder="1305"
                  disabled={isEdit}
                  maxLength={18}
                  className="mono"
                />
              </Field>
              <Field label={t('ar_field_msg')} hint={t('ar_field_msg_hint')}>
                <select
                  value={messageType}
                  onChange={(e) => setMessageType(e.target.value as RateMessageType | '')}
                  disabled={isEdit}
                  className="w-full h-9 px-3 text-[13px] rounded-md bg-surface border border-line text-text outline-none focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow disabled:opacity-60"
                >
                  <option value="">{t('ar_field_msg_any')}</option>
                  {MESSAGE_TYPES.map((m) => (
                    <option key={m} value={m}>
                      {t(`ar_msg_${m}`)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t('ar_field_priority')} hint={t('ar_field_priority_hint')}>
                <Input
                  type="number"
                  min="0"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="num"
                />
              </Field>
            </div>

            {!derivedCarrier && (
              <Field label={t('ar_field_carrier')} hint={t('ar_field_carrier_hint')}>
                <div className="flex items-center gap-2">
                  <select
                    value={providerId || ''}
                    onChange={(e) => setProviderId(Number(e.target.value) || 0)}
                    disabled={isEdit}
                    className="flex-1 h-9 px-3 text-[13px] rounded-md bg-surface border border-line text-text outline-none focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow disabled:opacity-60"
                  >
                    <option value="">{t('ar_field_carrier_choose')}</option>
                    {(providers.data?.items ?? []).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                  {selectedProvider && <CarrierHealthDot status={selectedProvider.health_status} />}
                </div>
              </Field>
            )}

            <div className={cn('grid gap-3', derivedCarrier ? 'grid-cols-1' : 'grid-cols-2')}>
              {!derivedCarrier && (
                <Field label={t('ar_field_buy_cost')}>
                  <Input
                    type="number"
                    step="0.000001"
                    min="0"
                    value={buyCost}
                    onChange={(e) => setBuyCost(e.target.value)}
                    placeholder="0.0050"
                    className="num"
                  />
                </Field>
              )}
              <Field
                label={t('ar_field_sell_price')}
                hint={derivedCarrier ? t('ar_field_sell_price_hint_reseller') : undefined}
              >
                <Input
                  type="number"
                  step="0.000001"
                  min="0"
                  value={sellPrice}
                  onChange={(e) => setSellPrice(e.target.value)}
                  placeholder="0.0120"
                  className="num"
                />
              </Field>
            </div>

            {!derivedCarrier && Number.isFinite(buyNum) && Number.isFinite(sellNum) && (
              <div className="text-[12px]">
                {sellBelowBuy ? (
                  <span className="text-danger">
                    {t('ar_margin_negative_warn', { value: margin.toFixed(6) })}
                  </span>
                ) : (
                  <span className="text-text-3">
                    {t('ar_margin_label')}{' '}
                    <span className="text-success font-medium">
                      +{margin.toFixed(6)} ({marginPct.toFixed(0)}%)
                    </span>
                  </span>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Field label={t('ar_field_effective_from')} hint={t('ar_field_effective_from_hint')}>
                <Input
                  type="datetime-local"
                  value={effectiveFrom}
                  onChange={(e) => setEffectiveFrom(e.target.value)}
                  disabled={isEdit}
                />
              </Field>
              <Field label={t('ar_field_effective_to')} hint={t('ar_field_effective_to_hint')}>
                <Input
                  type="datetime-local"
                  value={effectiveTo}
                  onChange={(e) => setEffectiveTo(e.target.value)}
                />
              </Field>
            </div>

            <Field label={t('ar_field_notes')}>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('ar_field_notes_placeholder')}
              />
            </Field>

            <label className="inline-flex items-center gap-2 text-[13px] text-text">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              {t('ar_field_active')}
            </label>
          </div>
          <div className="px-5 py-4 border-t border-line flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
              {t('ar_form_cancel')}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner />
                  <span>{t('ar_form_saving')}</span>
                </>
              ) : isEdit ? (
                t('ar_form_save')
              ) : (
                t('ar_form_create')
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: JSX.Element;
}): JSX.Element {
  return (
    <div>
      <label className="block text-[12px] font-medium text-text-2 mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-text-4 mt-1">{hint}</p>}
    </div>
  );
}

function CarrierHealthDot({ status }: { status: string }): JSX.Element {
  const { t } = useTranslation('default');
  switch (status) {
    case 'up':
      return <Badge variant="success">{t('ar_carrier_up')}</Badge>;
    case 'down':
      return <Badge variant="danger">{t('ar_carrier_down')}</Badge>;
    default:
      return <Badge variant="neutral">{t('ar_carrier_unknown')}</Badge>;
  }
}

// Format an ISO timestamp for an <input type="datetime-local"> value
// (which expects "YYYY-MM-DDTHH:MM" with no timezone).
function toDateInputValue(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number): string => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
